import { Link, Navigate, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import {
  useAuth,
  useSwitchJobTelemetry,
  isInternalTrace,
} from '@nyos/shared'
import { useYard } from '../../../contexts/YardContext'
import ScreenShell from '../../../components/ScreenShell'

/**
 * Engineer trace (Plan 26 Phase 1b) — SUPERADMIN-only solver internals for
 * one work order. Route guard bounces non-superadmins; the server is the
 * real gate — it never returns InternalSolverTraceDTO to other roles, so a
 * spoofed URL just renders the customer-shape fields below (no internals).
 */
export default function SwitchTracePage() {
  const { id: workOrderId } = useParams<{ id: string }>()
  const { activeFacilityId } = useYard()
  const { user } = useAuth()

  if (user?.role !== 'ROLE_SUPERADMIN') {
    return <Navigate to="/" replace />
  }

  return <TraceBody workOrderId={workOrderId} facilityId={activeFacilityId} />
}

function TraceBody({
  workOrderId,
  facilityId,
}: {
  workOrderId?: string
  facilityId?: string
}) {
  const telemetry = useSwitchJobTelemetry(facilityId, workOrderId)

  return (
    <ScreenShell
      title="Switch Trace"
      subtitle="Engine internals: every rule evaluated, every constraint applied (SUPERADMIN)"
      actions={
        <Link
          to={workOrderId ? `/execution/switch-jobs/${workOrderId}/story` : '/insights'}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Story
        </Link>
      }
    >
      <div className="h-full overflow-y-auto p-4 max-w-3xl space-y-4">
        {telemetry.isPending ? (
          <div className="text-sm text-muted-foreground">Loading trace…</div>
        ) : telemetry.isError ? (
          <div className="text-sm text-destructive">Couldn't load trace — try again.</div>
        ) : telemetry.data == null ? (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-sm">
            No telemetry row for this work order.
          </div>
        ) : !isInternalTrace(telemetry.data) ? (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-sm">
            The server returned the customer DTO — internal fields are not
            available under this session's claims.
          </div>
        ) : (
          <InternalDump trace={telemetry.data} />
        )}
      </div>
    </ScreenShell>
  )
}

function InternalDump({ trace }: { trace: import('@nyos/shared').InternalSolverTrace }) {
  return (
    <>
      <section className="rounded-lg border border-border bg-card p-4">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
          Generation record
        </h3>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-xs font-mono">
          <Row label="telemetry_id" value={trace.id} />
          <Row label="work_order" value={trace.workOrderId} />
          <Row label="facility" value={trace.facilityId} />
          <Row label="generation_version" value={`v${trace.generationVersion}`} />
          <Row label="engine_version" value={trace.engineVersion} />
          <Row label="status" value={trace.executionStatus} />
          <Row label="created_by" value={trace.createdBy ?? '—'} />
          <Row label="created_at" value={new Date(trace.createdAt).toLocaleString()} />
          <Row label="payload_hash" value={trace.payloadHash} mono />
        </dl>
      </section>

      {trace.errorDiagnostic && Object.keys(trace.errorDiagnostic).length > 0 && (
        <section className="rounded-lg border border-destructive/40 bg-destructive/5 p-4">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-destructive mb-3">
            Error diagnostic
          </h3>
          <pre className="text-xs font-mono overflow-x-auto whitespace-pre-wrap">
            {JSON.stringify(trace.errorDiagnostic, null, 2)}
          </pre>
        </section>
      )}

      <section className="rounded-lg border border-border bg-card p-4">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
          Metrics (raw)
        </h3>
        <pre className="text-xs font-mono overflow-x-auto whitespace-pre-wrap text-muted-foreground">
          {JSON.stringify(
            { measured: trace.measuredMetrics ?? {}, modeled: trace.modeledMetrics ?? {} },
            null,
            2
          )}
        </pre>
      </section>

      <section className="rounded-lg border border-border bg-card p-4">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
          Reason codes (raw)
        </h3>
        <pre className="text-xs font-mono overflow-x-auto whitespace-pre-wrap text-muted-foreground">
          {JSON.stringify(trace.reasonCodes, null, 2)}
        </pre>
      </section>
    </>
  )
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={mono ? 'break-all' : 'break-all'}>{value}</dd>
    </>
  )
}
